import { isQualifiedBand } from '../constants/staff-bands';
import {
  analyticsPageUrl,
  registrationEventParameters,
  type RegistrationTracking,
} from './registration-tracking';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

const SIGNUP_VALUE = 1;
const QUALIFIED_SIGNUP_VALUE = 500;
const CURRENCY = 'ZAR';
// Matches the corporate site's configured measurement ID; GA Admin must still
// verify its property mapping and configure these two domains before launch.
const MARKETING_GA_ID =
  import.meta.env.VITE_MARKETING_GA_MEASUREMENT_ID ?? 'G-G3XGLRHTQ2';
const legacyRegistrationSetting = (
  import.meta.env.VITE_ENABLE_LEGACY_REGISTRATION_CONVERSIONS ?? ''
)
  .trim()
  .toLowerCase();
// Match the corporate site: only explicit false/0 retires legacy signals.
// Missing or unrecognised settings preserve reporting until the Ads cutover.
const LEGACY_REGISTRATION_CONVERSIONS = !['false', '0'].includes(
  legacyRegistrationSetting,
);
const completedInThisPage = new WeakSet<RegistrationTracking>();
let initialized = false;
let lastPageLocation: string | null = null;

function getTrackingIds() {
  return [
    ...new Set(
      [
        MARKETING_GA_ID,
        import.meta.env.VITE_GA_MEASUREMENT_ID,
        import.meta.env.VITE_GOOGLE_ADS_ID,
      ].filter(
        (id): id is string =>
          typeof id === 'string' && /^(G-[A-Z0-9]+|AW-\d+)$/.test(id),
      ),
    ),
  ];
}

export function initAnalytics() {
  if (
    !import.meta.env.PROD ||
    import.meta.env.VITE_ENABLE_ANALYTICS === 'false'
  )
    return false;
  if (initialized) return true;

  const trackingIds = getTrackingIds();
  if (!trackingIds.length) return false;

  window.dataLayer = window.dataLayer || [];
  // gtag.js ignores plain arrays. The shim must push arguments, as Google's snippet does.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag('js', new Date());
  window.gtag('set', 'linker', {
    domains: ['llaami.com', 'dashboard.llaami.com'],
  });

  const pageContext = {
    page_location: analyticsPageUrl(window.location.href),
  };
  window.gtag('set', pageContext);
  trackingIds.forEach((id) =>
    window.gtag?.('config', id, { ...pageContext, send_page_view: false }),
  );

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${trackingIds[0]}`;
  document.head.appendChild(script);
  initialized = true;
  return true;
}

type RouterPageState = {
  location: { pathname: string; search: string };
  navigation: { state: string };
};

export function getRouterPageUrl(state: RouterPageState, origin: string) {
  if (state.navigation.state !== 'idle') return null;
  const { pathname, search } = state.location;
  return new URL(`${pathname}${search}`, origin).href;
}

export function trackPageView(rawUrl = window.location.href) {
  const pageLocation = analyticsPageUrl(rawUrl);
  if (!pageLocation || !initAnalytics() || !window.gtag) return false;

  window.gtag('set', { page_location: pageLocation });
  if (pageLocation === lastPageLocation) return false;
  // Ads builds its remarketing lists from page views, so it gets them too.
  window.gtag('event', 'page_view', {
    send_to: getTrackingIds(),
    page_location: pageLocation,
    ...(lastPageLocation === null
      ? { page_referrer: analyticsPageUrl(document.referrer) }
      : {}),
  });
  lastPageLocation = pageLocation;
  return true;
}

export function trackRegistrationComplete(registration: RegistrationTracking) {
  if (!['business', 'free-mail'].includes(registration.emailDomainType))
    return false;
  // The success view keeps this exact state object through repeated effects.
  // A reload has no success state, so no identifier or browser storage is needed.
  if (completedInThisPage.has(registration)) return false;

  const gaIds = getTrackingIds().filter((id) => id.startsWith('G-'));
  if (!gaIds.length || !initAnalytics() || !window.gtag) return false;

  const parameters = registrationEventParameters(registration);
  window.gtag('event', 'registration_complete', {
    ...parameters,
    send_to: gaIds,
    page_location: analyticsPageUrl(window.location.href),
  });
  completedInThisPage.add(registration);
  return true;
}

export function trackSignup(method: string, staffBand: string) {
  // The explicit preview switch disables all analytics, including legacy Ads.
  // Otherwise legacy reporting remains independent of new-event validation.
  if (!LEGACY_REGISTRATION_CONVERSIONS || !initAnalytics() || !window.gtag)
    return false;
  window.gtag('set', {
    page_location: analyticsPageUrl(window.location.href),
  });
  window.gtag?.('event', 'sign_up', {
    method,
    staff_band: staffBand,
    value: SIGNUP_VALUE,
    currency: CURRENCY,
  });

  reportAdsConversion(
    import.meta.env.VITE_GOOGLE_ADS_SIGNUP_LABEL,
    SIGNUP_VALUE,
  );

  if (!isQualifiedBand(staffBand)) return true;

  window.gtag?.('event', 'sign_up_qualified', {
    method,
    staff_band: staffBand,
    value: QUALIFIED_SIGNUP_VALUE,
    currency: CURRENCY,
  });

  reportAdsConversion(
    import.meta.env.VITE_GOOGLE_ADS_QUALIFIED_SIGNUP_LABEL,
    QUALIFIED_SIGNUP_VALUE,
  );
  return true;
}

function reportAdsConversion(sendTo: string | undefined, value: number) {
  if (!sendTo) return;

  window.gtag?.('event', 'conversion', {
    send_to: sendTo,
    value,
    currency: CURRENCY,
  });
}
